import type { CellValue, InputsReportContext, TableColumnSpec, TableRow } from './types';
import { normalizeRow } from './tableRows';

/**
 * Input sheets: what a report asks the person filling it in for.
 *
 * A bundle's `inputs.json` is its input sheet — sections of inputs, each with
 * an identifier the report reads it by. The studio's Inputs tab designs it;
 * the fill page draws a form from it; the report receives what was entered,
 * as `context.inputs`, typed by the `inputs.generated.ts` the server writes
 * from the sheet on every save. Someone may type the inputs in by hand today;
 * a procedure run may fill them in later. Either way the report reads the
 * same inputs, so where they came from never reaches its code.
 *
 * The sheet is JSON only — it crosses the sandbox and is stored with every
 * compiled bundle — and everything here is pure.
 */

export type InputKind = 'number' | 'text' | 'choice' | 'yesno' | 'date' | 'names' | 'numbers' | 'image' | 'file' | 'waveform' | 'table' | 'sweep';

interface InputCommon {
  /** what the report reads it by: lowercase letters, digits and underscores, starting with a letter */
  id: string;
  /** the question the operator is asked */
  label: string;
  help?: string;
  /** publishing waits until it is filled in */
  required?: boolean;
  /** copied into a new report started from this one ("keep the unit and setup") */
  carryOver?: boolean;
}

export interface NumberInput extends InputCommon {
  kind: 'number';
  unit?: string;
  placeholder?: string;
}

export interface TextInput extends InputCommon {
  kind: 'text';
  multiline?: boolean;
  placeholder?: string;
}

export interface ChoiceInput extends InputCommon {
  kind: 'choice';
  choices: string[];
}

export interface YesNoInput extends InputCommon {
  kind: 'yesno';
}

/** A calendar date, stored as `YYYY-MM-DD`. */
export interface DateInput extends InputCommon {
  kind: 'date';
}

/** A list of names entered one at a time, e.g. the functions tested. A sweep can take a condition's values from it. */
export interface NamesInput extends InputCommon {
  kind: 'names';
  /** what a new report starts with */
  defaults?: string[];
  /** the defaults are the only names allowed: the operator picks some or all of them, but cannot add their own */
  onlyDefaults?: boolean;
}

/** A list of numbers entered one at a time, e.g. the supply voltages. A sweep can take a condition's values from it. */
export interface NumbersInput extends InputCommon {
  kind: 'numbers';
  unit?: string;
  defaults?: number[];
  /** the defaults are the only numbers allowed: the operator picks some or all of them, but cannot add their own */
  onlyDefaults?: boolean;
}

export interface ImageInput extends InputCommon {
  kind: 'image';
  /** more than one image; one at most otherwise */
  multiple?: boolean;
}

export interface FileInput extends InputCommon {
  kind: 'file';
  multiple?: boolean;
  /** extensions it takes, e.g. [".set"]; any file when absent */
  accept?: string[];
}

/** A capture: a CSV or scope export of x against y, which the report receives as a trace. */
export interface WaveformInput extends InputCommon {
  kind: 'waveform';
  /** what the columns are, for the operator: e.g. "time (s)" and "current (A)" */
  xLabel?: string;
  yLabel?: string;
}

/** Rows of typed cells; the operator adds rows. */
export interface TableInput extends InputCommon {
  kind: 'table';
  columns: TableInputColumn[];
  /** the rows a new report starts with, e.g. one per instrument. Their file columns start empty. */
  rows?: TableInputRow[];
}

/** A table column the operator uploads into, as a sweep cell records an image, file or capture. */
export interface FileColumnSpec {
  id: string;
  title: string;
  type: 'image' | 'file' | 'waveform';
  help?: string;
  /** a CSS width for the column, e.g. "8rem" */
  width?: string;
  /** image or file: more than one in a cell; one at most otherwise */
  multiple?: boolean;
  /** file: extensions it takes, e.g. [".set"]; any file when absent */
  accept?: string[];
  /** waveform: what the columns of the capture are, for the operator */
  xLabel?: string;
  yLabel?: string;
}

export type TableInputColumn = TableColumnSpec | FileColumnSpec;

/** A table input's row: typed cells, and in its file columns `FileRef[]` (image, file) or `FileRef | null` (waveform). */
export type TableInputRow = Record<string, CellValue | FileRef | FileRef[]>;

/**
 * One or more inputs recorded at every combination of some conditions —
 * a reading per function at each supply voltage, at each temperature.
 */
export interface SweepInput extends InputCommon {
  kind: 'sweep';
  conditions: SweepCondition[];
  /** what is recorded in each cell. `required` on the sweep makes each of them required unless it says `required: false`. */
  record: CellInput[];
  layout: SweepLayout;
}

/** What a sweep cell can record: single values and captures, not tables or sweeps. */
export type CellInput = NumberInput | TextInput | ChoiceInput | YesNoInput | DateInput | ImageInput | FileInput | WaveformInput;

/**
 * What a sweep changes. Its values are either fixed in the bundle
 * (`values`) or set by the report runner, taken from a names or numbers input on
 * the sheet (`input`), whose defaults are where a new report starts — or,
 * when it is `onlyDefaults`, the only values the operator can pick from.
 */
export interface SweepCondition {
  id: string;
  label: string;
  /** `names` or `numbers`; taken from the input when the values are set by the report runner */
  kind?: 'names' | 'numbers';
  unit?: string;
  values?: Array<string | number>;
  /** the id of the names or numbers input the operator sets these at */
  input?: string;
}

/**
 * Where each condition goes on the fill page. Only presentation: moving a
 * condition between shelves changes nothing the report receives. In the flat
 * table the shelves set the row order (tabs change slowest, columns fastest).
 */
export interface SweepLayout {
  tabs: string[];
  rows: string[];
  columns: string[];
  /** grid or one row per cell; chosen from the sweep's size when absent (sweepView) */
  view?: 'grid' | 'flat';
}

export type SheetInput = NumberInput | TextInput | ChoiceInput | YesNoInput | DateInput | NamesInput | NumbersInput | ImageInput | FileInput | WaveformInput | TableInput | SweepInput;

export interface InputSheetSection {
  id: string;
  title: string;
  help?: string;
  inputs: SheetInput[];
}

export interface InputSheet {
  sections: InputSheetSection[];
}

/** A sheet as `inputs.generated.ts` exports it: the sheet, carrying the type of what it collects. */
export type TypedInputSheet<I> = InputSheet & { readonly __inputs?: I };

/** The sheet in `inputs.json`, typed by the interface generated from it. */
export function typedSheet<I>(raw: unknown): TypedInputSheet<I> {
  return raw as TypedInputSheet<I>;
}

// ── values ──────────────────────────────────────────────────────────────────

/** An uploaded file, as an image, file or waveform input holds it. */
export interface FileRef {
  fileId: string;
  fileName: string;
}

export type ConditionValue = string | number;

/** One combination of a sweep's conditions and what was recorded there; an unrecorded input is absent or null. */
export interface SweepCell<A = Record<string, ConditionValue>, R = Record<string, unknown>> {
  at: A;
  values: Partial<R>;
}

export interface SweepValue<A = Record<string, ConditionValue>, R = Record<string, unknown>> {
  cells: Array<SweepCell<A, R>>;
}

/** What was entered on a sheet, by input id. */
export type InputValues = Record<string, unknown>;

export const INPUT_KINDS: readonly InputKind[] = ['number', 'text', 'choice', 'yesno', 'date', 'names', 'numbers', 'image', 'file', 'waveform', 'table', 'sweep'];
export const CELL_INPUT_KINDS: readonly InputKind[] = ['number', 'text', 'choice', 'yesno', 'date', 'image', 'file', 'waveform'];
/** kinds whose value is one or more uploaded files */
export const FILE_INPUT_KINDS: readonly InputKind[] = ['image', 'file', 'waveform'];
/** table column types whose cells are uploaded files */
export const FILE_COLUMN_TYPES: ReadonlyArray<FileColumnSpec['type']> = ['image', 'file', 'waveform'];

export function isFileColumn(column: TableInputColumn): column is FileColumnSpec {
  return (FILE_COLUMN_TYPES as readonly string[]).includes(column.type);
}

/** A file column as the input its cells hold: what normalizes, checks and edits them, as a sweep cell's would. */
export function columnInput(column: FileColumnSpec): ImageInput | FileInput | WaveformInput {
  const common = { id: column.id, label: column.title, ...(column.help ? { help: column.help } : {}) };
  switch (column.type) {
    case 'image': return { ...common, kind: 'image', multiple: column.multiple };
    case 'file': return { ...common, kind: 'file', multiple: column.multiple, accept: column.accept };
    case 'waveform': return { ...common, kind: 'waveform', xLabel: column.xLabel, yLabel: column.yLabel };
  }
}

export const INPUT_ID_PATTERN = /^[a-z][a-z0-9_]{0,59}$/;

const isObject = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);

function toNumber(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw.trim().replace('−', '-'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toFileRef(raw: unknown): FileRef | null {
  if (!isObject(raw) || typeof raw.fileId !== 'string' || raw.fileId === '') return null;
  return { fileId: raw.fileId, fileName: typeof raw.fileName === 'string' ? raw.fileName : '' };
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Of the allowed values, those picked, in the allowed order: what an `onlyDefaults` list holds. */
function onlyAllowed<T>(allowed: T[], picked: T[]): T[] {
  return allowed.filter((x) => picked.includes(x));
}

/** One input's value brought to its kind's shape: what a new report holds when `raw` is undefined. */
export function normalizeInputValue(input: SheetInput, raw: unknown): unknown {
  switch (input.kind) {
    case 'number':
      return raw === undefined ? null : toNumber(raw);
    case 'text':
      return typeof raw === 'string' ? raw : typeof raw === 'number' ? String(raw) : '';
    case 'choice':
      return typeof raw === 'string' && input.choices.includes(raw) ? raw : '';
    case 'yesno':
      return typeof raw === 'boolean' ? raw : raw === 'yes' || raw === 'true' ? true : raw === 'no' || raw === 'false' ? false : null;
    case 'date':
      return typeof raw === 'string' && DATE.test(raw) ? raw : '';
    case 'names': {
      if (raw === undefined) return [...(input.defaults ?? [])];
      const names = Array.isArray(raw) ? Array.from(new Set(raw.filter((x): x is string => typeof x === 'string').map((x) => x.trim()).filter(Boolean))) : [];
      return input.onlyDefaults ? onlyAllowed(input.defaults ?? [], names) : names;
    }
    case 'numbers': {
      if (raw === undefined) return [...(input.defaults ?? [])];
      if (!Array.isArray(raw)) return [];
      const numbers = Array.from(new Set(raw.map(toNumber).filter((x): x is number => x !== null)));
      return input.onlyDefaults ? onlyAllowed(input.defaults ?? [], numbers) : numbers;
    }
    case 'image':
    case 'file': {
      const files = (Array.isArray(raw) ? raw : raw === undefined || raw === null ? [] : [raw]).map(toFileRef).filter((f): f is FileRef => f !== null);
      return input.multiple ? files : files.slice(0, 1);
    }
    case 'waveform':
      return toFileRef(raw);
    case 'table':
      return raw === undefined ? normalizeTableRows(input.columns, input.rows ?? [], false) : normalizeTableRows(input.columns, raw);
    case 'sweep':
      return normalizeSweep(input, raw);
  }
}

/**
 * A table's rows brought to its columns: unknown keys dropped, typed cells
 * coerced, file cells as their input holds them. Without `files`, the file
 * columns come out empty — what starting rows give a new report, since a
 * file belongs to the report it was uploaded against.
 */
export function normalizeTableRows(columns: TableInputColumn[], raw: unknown, files = true): TableInputRow[] {
  if (!Array.isArray(raw)) return [];
  const typed = columns.filter((c): c is TableColumnSpec => !isFileColumn(c));
  return raw.map((r) => {
    const source = isObject(r) ? r : {};
    const plain: TableRow = normalizeRow(typed, source);
    return Object.fromEntries(columns.map((c) => [c.id, isFileColumn(c) ? normalizeInputValue(columnInput(c), files ? source[c.id] : undefined) : plain[c.id]])) as TableInputRow;
  });
}

/** A cell with nothing in it: null, blank, or no files. */
const isBlankCell = (cell: unknown) => cell === null || cell === undefined || cell === '' || (Array.isArray(cell) && cell.length === 0);

function normalizeSweep(sweep: SweepInput, raw: unknown): SweepValue {
  const cells = isObject(raw) && Array.isArray(raw.cells) ? raw.cells : [];
  const byKey = new Map<string, SweepCell>();
  for (const cell of cells) {
    if (!isObject(cell) || !isObject(cell.at)) continue;
    const at: Record<string, ConditionValue> = {};
    for (const condition of sweep.conditions) {
      const value = cell.at[condition.id];
      if (typeof value === 'string' || typeof value === 'number') at[condition.id] = value;
    }
    const source = isObject(cell.values) ? cell.values : {};
    const values: Record<string, unknown> = {};
    for (const input of sweep.record) {
      if (source[input.id] === undefined) continue;
      const value = normalizeInputValue(input, source[input.id]);
      if (isInputFilled(input, value)) values[input.id] = value;
    }
    if (Object.keys(values).length === 0) continue;
    byKey.set(cellKey(sweep, at), { at, values });
  }
  return { cells: [...byKey.values()] };
}

/** Every input on the sheet in order, sections flattened. */
export function sheetInputs(sheet: InputSheet): SheetInput[] {
  return sheet.sections.flatMap((s) => s.inputs);
}

export function findInput(sheet: InputSheet, id: string): SheetInput | undefined {
  return sheetInputs(sheet).find((i) => i.id === id);
}

/** What a new report starts with: every input empty, but for the defaults a names, numbers or table input brings. */
export function emptyInputs(sheet: InputSheet): InputValues {
  return Object.fromEntries(sheetInputs(sheet).map((input) => [input.id, normalizeInputValue(input, undefined)]));
}

/**
 * Stored inputs brought up to the sheet. Values the sheet no longer asks for
 * are kept as stored, so a report filled against an earlier version of the
 * sheet loses nothing when the sheet changes.
 */
export function normalizeInputs(sheet: InputSheet, raw: unknown): InputValues {
  const source = isObject(raw) ? raw : {};
  const known = Object.fromEntries(sheetInputs(sheet).map((input) => [input.id, normalizeInputValue(input, source[input.id])]));
  return { ...source, ...known };
}

/** The inputs a new report started from `previous` keeps: those marked `carryOver`. The rest start empty. */
export function carryOverInputs(sheet: InputSheet, previous: unknown): InputValues {
  const from = normalizeInputs(sheet, previous);
  const fresh = emptyInputs(sheet);
  return Object.fromEntries(sheetInputs(sheet).map((input) => [input.id, input.carryOver ? from[input.id] : fresh[input.id]]));
}

export function isInputFilled(input: SheetInput, value: unknown): boolean {
  if (value === null || value === undefined) return false;
  switch (input.kind) {
    case 'text':
    case 'choice':
    case 'date':
      return typeof value === 'string' && value.trim() !== '';
    case 'names':
    case 'numbers':
    case 'image':
    case 'file':
      return Array.isArray(value) && value.length > 0;
    case 'table':
      return Array.isArray(value) && value.some((row) => isObject(row) && Object.values(row).some((c) => !isBlankCell(c)));
    case 'sweep':
      return isObject(value) && Array.isArray(value.cells) && value.cells.length > 0;
    default:
      return true;
  }
}

/**
 * The context a report filled in from `sheet` is judged in: what was entered
 * (nothing yet, when `inputs` is undefined), normalized to the sheet. How the
 * compiler smoke-tests a definition and runs its fixtures, and how the studio
 * previews one.
 */
export function inputsContext(sheet: InputSheet, inputs?: unknown, traces?: unknown, report: InputsReportContext['report'] = null): InputsReportContext {
  return {
    sheet,
    inputs: inputs === undefined ? emptyInputs(sheet) : normalizeInputs(sheet, inputs),
    traces: isObject(traces) ? (traces as InputsReportContext['traces']) : {},
    report,
  };
}

// ── sweeps ──────────────────────────────────────────────────────────────────

export interface ResolvedCondition {
  id: string;
  label: string;
  kind: 'names' | 'numbers';
  unit: string | undefined;
  values: ConditionValue[];
  /** the input the operator sets them at; null when the bundle fixes them */
  input: string | null;
}

/** A sweep's conditions with their values: the bundle's, or what the report runner set. */
export function resolveConditions(sheet: InputSheet, sweep: SweepInput, inputs: object): ResolvedCondition[] {
  const entered = inputs as InputValues;
  return sweep.conditions.map((condition) => {
    const source = condition.input ? findInput(sheet, condition.input) : undefined;
    if (source && (source.kind === 'names' || source.kind === 'numbers')) {
      const values = normalizeInputValue(source, entered[source.id]) as ConditionValue[];
      return { id: condition.id, label: condition.label || source.label, kind: source.kind, unit: condition.unit ?? (source.kind === 'numbers' ? source.unit : undefined), values, input: source.id };
    }
    const kind = condition.kind ?? (condition.values?.every((x) => typeof x === 'number') ? 'numbers' : 'names');
    const values = (condition.values ?? []).map((x) => (kind === 'numbers' ? toNumber(x) : typeof x === 'string' ? x : String(x))).filter((x): x is ConditionValue => x !== null && x !== '');
    return { id: condition.id, label: condition.label, kind, unit: condition.unit, values, input: null };
  });
}

/** A cell's position as a string, in the sweep's condition order. Numbers and numeric strings key alike. */
export function cellKey(sweep: Pick<SweepInput, 'conditions'>, at: Record<string, ConditionValue | undefined>): string {
  return JSON.stringify(sweep.conditions.map((c) => {
    const value = at[c.id];
    if (value === undefined) return null;
    const n = toNumber(value);
    return n !== null && c.kind !== 'names' ? n : value;
  }));
}

/** A condition value as it prints: a number with its unit, a name as is. */
export function formatConditionValue(condition: Pick<ResolvedCondition, 'unit'>, value: ConditionValue): string {
  return typeof value === 'number' && condition.unit ? `${value} ${condition.unit}` : String(value);
}

/** How a cell is named in a list: "DRL at 16 V", "Low beam at 9 V, −40 °C". */
export function describeCell(conditions: ResolvedCondition[], at: Record<string, ConditionValue>): string {
  const parts = conditions.map((c) => (at[c.id] === undefined ? '' : formatConditionValue(c, at[c.id]))).filter(Boolean);
  return parts.length <= 1 ? parts.join('') : `${parts[0]} at ${parts.slice(1).join(', ')}`;
}

export interface GridCell {
  key: string;
  at: Record<string, ConditionValue>;
  values: Record<string, unknown>;
}

export interface SweepGrid {
  conditions: ResolvedCondition[];
  /** every combination of the conditions' current values, tabs changing slowest and columns fastest */
  cells: GridCell[];
  /** the cell at a combination, recorded or not */
  cell: (at: Record<string, ConditionValue>) => GridCell;
}

/** A shelf order over the conditions: tabs, then rows, then columns; any condition the layout leaves out goes last. */
export function shelfOrder(sweep: SweepInput): string[] {
  const placed = [...sweep.layout.tabs, ...sweep.layout.rows, ...sweep.layout.columns];
  return [...placed.filter((id) => sweep.conditions.some((c) => c.id === id)), ...sweep.conditions.map((c) => c.id).filter((id) => !placed.includes(id))];
}

/** A sweep as a grid of cells over its conditions' current values, with what was recorded in each. */
export function sweepGrid(sheet: InputSheet, sweep: SweepInput, inputs: object): SweepGrid {
  const conditions = resolveConditions(sheet, sweep, inputs);
  const value = normalizeSweep(sweep, (inputs as InputValues)[sweep.id]);
  const recorded = new Map(value.cells.map((c) => [cellKey(sweep, c.at), c]));
  const byId = new Map(conditions.map((c) => [c.id, c]));
  const order = shelfOrder(sweep).map((id) => byId.get(id)).filter((c): c is ResolvedCondition => !!c);
  let combos: Array<Record<string, ConditionValue>> = [{}];
  for (const condition of order) combos = combos.flatMap((at) => condition.values.map((v) => ({ ...at, [condition.id]: v })));
  if (order.some((c) => c.values.length === 0)) combos = [];
  const make = (at: Record<string, ConditionValue>): GridCell => {
    const key = cellKey(sweep, at);
    return { key, at, values: (recorded.get(key)?.values as Record<string, unknown>) ?? {} };
  };
  return { conditions, cells: combos.map(make), cell: make };
}

/** Grid or flat table: what the sweep says, or flat once a cell records more than three inputs or any file. */
export function sweepView(sweep: SweepInput): 'grid' | 'flat' {
  if (sweep.layout.view) return sweep.layout.view;
  return sweep.record.length > 3 || sweep.record.some((r) => FILE_INPUT_KINDS.includes(r.kind)) ? 'flat' : 'grid';
}

/** A sweep's value with one cell's input set (or cleared, for an empty value). */
export function setSweepCell(sweep: SweepInput, current: unknown, at: Record<string, ConditionValue>, inputId: string, value: unknown): SweepValue {
  const sweepValue = normalizeSweep(sweep, current);
  const key = cellKey(sweep, at);
  const input = sweep.record.find((r) => r.id === inputId);
  const cells = sweepValue.cells.filter((c) => cellKey(sweep, c.at) !== key);
  const existing = sweepValue.cells.find((c) => cellKey(sweep, c.at) === key);
  const values: Record<string, unknown> = { ...(existing?.values ?? {}) };
  if (input && isInputFilled(input, normalizeInputValue(input, value))) values[inputId] = normalizeInputValue(input, value);
  else delete values[inputId];
  return { cells: Object.keys(values).length ? [...cells, { at, values }] : cells };
}

/** One cell input of a sweep set (or cleared) on a fill page, for a page that saves only the cell inputs it changed. */
export interface SweepCellChange {
  /** the cell, by its conditions' values */
  at: Record<string, ConditionValue>;
  field: string;
  value: unknown;
}

/** Cell inputs of a sweep a save sets or clears: the cell, by its conditions' values, and which of what it records. */
export interface SweepCellFields {
  at: Record<string, ConditionValue>;
  fields: string[];
}

/**
 * A stored sweep with only the cell inputs `named` taken from `sent`: one
 * `sent` leaves empty is cleared, and a cell left recording nothing goes.
 * Cells are matched by their conditions' values on this sweep, whatever order
 * the sender's sheet put the conditions in. Each sweep is normalized once, so
 * this is linear in the cells.
 */
export function mergeSweepCells(sweep: SweepInput, stored: unknown, sent: unknown, named: SweepCellFields[]): SweepValue {
  const byKey = (value: SweepValue) => new Map(value.cells.map((c) => [cellKey(sweep, c.at), c]));
  const from = byKey(normalizeSweep(sweep, sent));
  const cells = byKey(normalizeSweep(sweep, stored));
  const recorded = new Set(sweep.record.map((r) => r.id));
  for (const { at, fields } of named) {
    const key = cellKey(sweep, at);
    const given = from.get(key);
    const existing = cells.get(key);
    const values: Record<string, unknown> = { ...existing?.values };
    for (const field of fields) {
      // what `sent` holds of it is normalized, and only there when filled in
      if (recorded.has(field) && given?.values[field] !== undefined) values[field] = given.values[field];
      else delete values[field];
    }
    // the cell's own position where it has one, so numbers stay numbers
    if (Object.keys(values).length) cells.set(key, { at: existing?.at ?? given?.at ?? at, values });
    else cells.delete(key);
  }
  return { cells: [...cells.values()] };
}

// ── progress ────────────────────────────────────────────────────────────────

/** One thing the operator fills in: an input, or one input of one sweep cell. */
export interface InputSlot {
  /** the input on the sheet */
  input: string;
  /** the sweep cell, for a sweep's slots */
  cell?: string;
  /** the recorded input within the cell */
  field?: string;
  label: string;
  filled: boolean;
  required: boolean;
  /** slots that print together in a compact view: a sweep's slots by their row */
  group?: string;
}

export interface SectionProgress {
  id: string;
  title: string;
  total: number;
  filled: number;
  slots: InputSlot[];
}

export interface SheetProgress {
  total: number;
  filled: number;
  sections: SectionProgress[];
  /** required slots not yet filled: what stands between the report and publishing */
  missing: InputSlot[];
}

function sweepSlots(sheet: InputSheet, sweep: SweepInput, inputs: InputValues): InputSlot[] {
  const grid = sweepGrid(sheet, sweep, inputs);
  const rowIds = sweep.layout.tabs.concat(sweep.layout.rows);
  return grid.cells.flatMap((cell) => sweep.record.map((field) => ({
    input: sweep.id,
    cell: cell.key,
    field: field.id,
    label: `${sweep.record.length > 1 ? field.label : sweep.label} · ${describeCell(grid.conditions, cell.at)}`,
    filled: isInputFilled(field, cell.values[field.id]),
    required: !!sweep.required && field.required !== false,
    group: JSON.stringify(rowIds.map((id) => cell.at[id] ?? null)),
  })));
}

/** How far the sheet is filled in, by section, and what publishing still waits for. */
export function sheetProgress(sheet: InputSheet, rawInputs: unknown): SheetProgress {
  const inputs = normalizeInputs(sheet, rawInputs);
  const sections = sheet.sections.map((section) => {
    const slots = section.inputs.flatMap((input): InputSlot[] => (input.kind === 'sweep'
      ? sweepSlots(sheet, input, inputs)
      : [{ input: input.id, label: input.label, filled: isInputFilled(input, inputs[input.id]), required: !!input.required }]));
    return { id: section.id, title: section.title, total: slots.length, filled: slots.filter((s) => s.filled).length, slots };
  });
  const all = sections.flatMap((s) => s.slots);
  return { total: all.length, filled: all.filter((s) => s.filled).length, sections, missing: all.filter((s) => s.required && !s.filled) };
}

// ── captures ────────────────────────────────────────────────────────────────

/**
 * Where a capture's trace lands in `context.traces`: the input's id; for a
 * sweep cell, `sweep:cellKey:field`; for a table cell, `table:rowIndex:column`.
 */
export function traceKey(input: string, cell?: string, field?: string): string {
  return cell === undefined ? input : `${input}:${cell}:${field ?? ''}`;
}

/** Every waveform entered on the sheet, with the trace key the report finds it under. */
export function sheetWaveforms(sheet: InputSheet, rawInputs: unknown): Array<{ key: string; file: FileRef }> {
  const inputs = normalizeInputs(sheet, rawInputs);
  const found: Array<{ key: string; file: FileRef }> = [];
  for (const input of sheetInputs(sheet)) {
    if (input.kind === 'waveform') {
      const file = inputs[input.id] as FileRef | null;
      if (file) found.push({ key: traceKey(input.id), file });
    } else if (input.kind === 'sweep') {
      const value = inputs[input.id] as SweepValue;
      for (const cell of value.cells) {
        for (const field of input.record) {
          if (field.kind !== 'waveform') continue;
          const file = toFileRef(cell.values[field.id]);
          if (file) found.push({ key: traceKey(input.id, cellKey(input, cell.at), field.id), file });
        }
      }
    } else if (input.kind === 'table') {
      (inputs[input.id] as TableInputRow[]).forEach((row, r) => {
        for (const column of input.columns) {
          if (column.type !== 'waveform') continue;
          const file = toFileRef(row[column.id]);
          if (file) found.push({ key: traceKey(input.id, String(r), column.id), file });
        }
      });
    }
  }
  return found;
}

/** Every uploaded file the inputs refer to, for checking they belong to the report's org. */
export function sheetFiles(sheet: InputSheet, rawInputs: unknown): FileRef[] {
  const inputs = normalizeInputs(sheet, rawInputs);
  const files: FileRef[] = [];
  const add = (input: SheetInput, value: unknown) => {
    if (input.kind === 'image' || input.kind === 'file') files.push(...(value as FileRef[]));
    else if (input.kind === 'waveform' && value) files.push(value as FileRef);
  };
  for (const input of sheetInputs(sheet)) {
    if (input.kind === 'sweep') {
      for (const cell of (inputs[input.id] as SweepValue).cells) for (const field of input.record) add(field, normalizeInputValue(field, cell.values[field.id]));
    } else if (input.kind === 'table') {
      const fileColumns = input.columns.filter(isFileColumn);
      for (const row of inputs[input.id] as TableInputRow[]) for (const column of fileColumns) add(columnInput(column), row[column.id]);
    } else add(input, inputs[input.id]);
  }
  return files;
}
